import {
  MaterialAssignmentModal as MaterialAssignmentModalUIComp,
  Material,
} from '@pelckmans/business-components/components/material-assignment-modal';
import { useContext, useEffect, useState } from 'react';
import getAccessToken from '../../../api/BearerToken';
import ModalContext from '../../../contexts/ModalContext';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../utils/formatDate';
import formatTeacherName from '../../../utils/formatTeacherName';
import { getExerciseAssignmentSession } from '../_api/MaterialApi';

export type MaterialAssignment = Material & {
  moduleId: string;
  assignment: {
    id: string;
    to: string;
  };
  owner: {
    firstName: string;
    lastName: string;
  };
};

type Props = {
  id: string;
  material: Omit<MaterialAssignment, 'isDone'>;
  onOpenMaterial: (material: Material) => void;
  onModalClosed: (material: Material) => void;
};

const tokenAccessor = { tokenGetter: getAccessToken };

export default function MaterialAssignmentModal({ id, material, onOpenMaterial, onModalClosed }: Props) {
  const [materialAssignment, setMaterialAssignment] = useState<MaterialAssignment>();

  const handleError = useThrowAsyncError();
  const { t } = useTranslation();

  const { closeModal } = useContext(ModalContext);

  useEffect(() => {
    async function fetchExerciseAssignmentSession() {
      const { result } = await getExerciseAssignmentSession(material.moduleId, material.assignment.id);

      setMaterialAssignment({ ...material, isDone: Boolean(result) });
    }

    fetchExerciseAssignmentSession().catch(handleError);
  }, [material, handleError]);

  if (!materialAssignment) return null;

  const handleModalClosed = () => {
    closeModal(id);
    onModalClosed(materialAssignment);
  };

  const handleSubmit = (e?: unknown) => {
    if (e) return handleError(e);

    handleModalClosed();
  };

  return (
    <MaterialAssignmentModalUIComp
      materials={[materialAssignment]}
      title={materialAssignment.name}
      sharedBy={formatTeacherName(materialAssignment.owner)}
      onClose={handleModalClosed}
      moduleId={materialAssignment.moduleId}
      assignmentId={materialAssignment.assignment.id}
      tokenAccessor={tokenAccessor}
      onSubmit={handleSubmit}
      onOpenMaterial={onOpenMaterial}
      translations={{
        close: t('ACTIONS.CLOSE'),
        cancel: t('ACTIONS.CANCEL'),
        confirm: t('ACTIONS.CONFIRM'),
        title: t('MODULES.MATERIAL.COMPONENTS.MATERIAL_ASSIGNMENT_MODAL.TITLE'),
        deadline: t('MODULES.MATERIAL.COMPONENTS.MATERIAL_ASSIGNMENT_MODAL.DEADLINE', {
          date: formatDate(material.assignment.to),
        }),
        open: t('MODULES.MATERIAL.COMPONENTS.MATERIAL_ASSIGNMENT_MODAL.OPEN'),
        isDone: t('MODULES.MATERIAL.COMPONENTS.MATERIAL_ASSIGNMENT_MODAL.IS_DONE'),
        task: t('MODULES.MATERIAL.COMPONENTS.MATERIAL_ASSIGNMENT_MODAL.TASK'),
      }}
      studioGroup={process.env.REACT_APP_P_STUDIO_GROUP}
    />
  );
}
